import { z } from "zod"

import {
  branchIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
  membershipIdSchema,
} from "./identifiers.js"

export const payrollReconciliationMismatchKindV1Schema = z.enum([
  "input",
  "run",
  "settlement",
  "accounting",
  "document",
])

export const payrollReconciliationStateV1Schema = z.enum([
  "open",
  "assigned",
  "retry_pending",
  "resolved",
])

export const payrollReconciliationRecordV1Schema = z.object({
  exceptionId: entityIdSchema,
  runId: entityIdSchema,
  mismatchKind: payrollReconciliationMismatchKindV1Schema,
  reasonCode: z.string().regex(/^[a-z][a-z0-9_]{2,63}$/),
  expectedFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  observedFingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  detectedAt: z.iso.datetime({ offset: true }),
}).strict().superRefine((value, context) => {
  if (value.expectedFingerprint === value.observedFingerprint) {
    context.addIssue({
      code: "custom",
      message: "A reconciliation exception requires different observations.",
    })
  }
})

export const payrollReconciliationManageV1Schema = z.discriminatedUnion(
  "action",
  [
    z.object({
      exceptionId: entityIdSchema,
      action: z.literal("assign"),
      assignedMembershipId: membershipIdSchema,
      reason: z.string().trim().min(1).max(500),
    }).strict(),
    z.object({
      exceptionId: entityIdSchema,
      action: z.literal("request_retry"),
      reason: z.string().trim().min(1).max(500),
    }).strict(),
    z.object({
      exceptionId: entityIdSchema,
      action: z.literal("resolve"),
      resolutionCode: z.enum([
        "provider_result_verified",
        "local_source_verified",
        "controlled_correction_required",
        "superseded_exception",
      ]),
      reason: z.string().trim().min(1).max(500),
    }).strict(),
  ],
)

export const payrollReconciliationWorkspaceV1Schema = z.object({
  state: payrollReconciliationStateV1Schema.optional(),
  limit: z.number().int().min(1).max(100),
}).strict()

export const payrollReconciliationItemV1Schema = z.object({
  exceptionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  runId: entityIdSchema,
  mismatchKind: payrollReconciliationMismatchKindV1Schema,
  reasonCode: z.string().regex(/^[a-z][a-z0-9_]{2,63}$/),
  expectedRef: z.string().regex(/^[a-f0-9]{16}$/),
  observedRef: z.string().regex(/^[a-f0-9]{16}$/),
  state: payrollReconciliationStateV1Schema,
  assignedMembershipId: membershipIdSchema.nullable(),
  retryCount: z.number().int().nonnegative(),
  resolutionCode: z.string().regex(/^[a-z][a-z0-9_]{2,63}$/).nullable(),
  detectedAt: z.iso.datetime({ offset: true }),
  updatedAt: z.iso.datetime({ offset: true }),
  version: z.number().int().positive(),
}).strict()

export const payrollReconciliationResultV1Schema = z.object({
  entityType: z.literal("payroll_reconciliation_exception"),
  entityId: entityIdSchema,
  state: payrollReconciliationStateV1Schema,
  version: z.number().int().positive(),
  item: payrollReconciliationItemV1Schema,
  sourceRecordsMutated: z.literal(false),
  audited: z.literal(true),
}).strict()

export const payrollReconciliationWorkspaceResultV1Schema = z.object({
  entityType: z.literal("payroll_reconciliation_workspace"),
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  items: z.array(payrollReconciliationItemV1Schema).max(100),
  mismatchKinds: z.array(payrollReconciliationMismatchKindV1Schema),
  sourceRecordsMutated: z.literal(false),
}).strict()

export type PayrollReconciliationItemV1 = z.infer<
  typeof payrollReconciliationItemV1Schema
>
